import type { FeedbackField } from '../FeedbackThumbs';
import type { AiAutoFilterItem, SearchResponseData, SearchResult } from './types';

function formatEntityType(type: string): string {
  return type.replace(/_/g, ' ');
}

/** Fields a user can single out when giving feedback on a search. */
export function buildSearchFeedbackFields(
  data: SearchResponseData,
  visibleResults: SearchResult[],
  appliedFilters: AiAutoFilterItem[],
): FeedbackField[] {
  const fields: FeedbackField[] = [
    {
      key: 'results',
      label: 'Results overall',
      value: `${visibleResults.length} result${visibleResults.length === 1 ? '' : 's'} for "${data.raw_query}"`,
      group: 'Search',
      badPrompt: 'What were you looking for?',
      asksExpectedRecord: true,
    },
  ];

  if (appliedFilters.length > 0) {
    fields.push({
      key: 'ai_auto_filters',
      label: 'AI auto filters',
      value: appliedFilters.map((filter) => `${filter.label}: ${filter.values.join(', ')}`).join('; '),
      group: 'Search',
    });
  }

  const seenKeys = new Set(fields.map((field) => field.key));
  for (const entity of data.pre_retrieval?.entity_recognition?.entities ?? []) {
    const key = `entity:${entity.type}:${entity.name}`;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    fields.push({
      key,
      label: `Detected ${formatEntityType(entity.type)}`,
      value: entity.name,
      group: 'Detected in your query',
      meta: { type: entity.type, name: entity.name, method: entity.method },
    });
  }

  const rankById = new Map(data.results.map((result, index) => [result.id, index + 1]));
  for (const result of visibleResults) {
    const rank = rankById.get(result.id) ?? null;
    fields.push({
      key: `result:${result.id}`,
      label: rank ? `Result #${rank}` : 'Result',
      value: result.title || result.file?.name,
      group: 'Results',
      meta: {
        record_id: result.id,
        rank,
        reranking_score: result.reranking_score ?? null,
        similarity_distance: result.similarity_distance ?? null,
      },
    });
  }

  return fields;
}
